import React, { useState, useRef, useEffect, useCallback } from 'react';
import './BadgeSlider.css';
import './styles/ipad.css';
import './styles/mobile.css';
import { BadgeSliderProps } from './BadgeSlider.props';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';
import AchievementsBadgePopover from '../AchievementsBadgePopover/AchievementsBadgePopover';
import {handleErrorImg, parseImgURLBadge, sortBadges} from '../../utils/util';

const BadgeAchievement: React.FC<BadgeSliderProps> = ({ badgeList = [] }) => {
  const swiperRef = useRef<SwiperClass | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [badgeData, setBadgeData] = useState<any>({});
  const [badgeListData, setBadgeListData] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [rowHeight, setRowHeight] = useState(0);

  const handleCloseHoverBadge = () => {
    setAnchorEl(null);
  };
  const handleClearBadgeData = () => {
    setBadgeData({});
  };

  const handleHoverBadge = (event: React.MouseEvent<HTMLElement>, val: any) => {
    setBadgeData(val);
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    const sortedBadgeList = sortBadges(badgeList);
    setBadgeListData(sortedBadgeList);

    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [badgeList]);

  const detectOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el || !el.children.length) return;

    const children = el.children;
    const firstChild = children[0] as HTMLElement;

    const gap = 12;
    const calculatedRowHeight = firstChild.offsetHeight + gap;
    setRowHeight(calculatedRowHeight);

    const firstRowTop = firstChild.offsetTop;

    let wrapped = false;
    for (let i = 1; i < children.length; i++) {
      if ((children[i] as HTMLElement).offsetTop > firstRowTop) {
        wrapped = true;
        break;
      }
    }

    setIsOverflow(wrapped);
  }, []);

  useEffect(() => {
    detectOverflow();
  }, [badgeListData, isExpanded, detectOverflow]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      detectOverflow();
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [detectOverflow]);

  return (
    <>
      <div style={{ width: '100%', overflow: 'visible' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
          <div style={{ width: '100%' }}>
            <div
              ref={containerRef}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                width: '100%',
                marginBottom: '8px',
                maxHeight: isExpanded ? 'none' : `${rowHeight}px`,
                overflow: 'hidden',
                transition: 'max-height 0.3s ease'
              }}
            >
              {badgeListData?.map((item: any, index: number) => (
                <div
                  className="badge-item-wrapper"
                  key={item.badgeId}
                  style={{ width: 'auto', flexShrink: 0 }}
                  onMouseEnter={(e) => handleHoverBadge(e, item)}
                  onMouseLeave={handleCloseHoverBadge}
                >
                  <img
                    style={{ height: 52, width: 52, display: 'block' }}
                    src={parseImgURLBadge(item.category, item.badgeId)}
                    alt={`badge-${index}`}
                    onError={handleErrorImg}
                    data-testid="badge-img"
                  />
                </div>
              ))}
            </div>

            {isOverflow && (
              <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  color: '#1e3a8a',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'inline-block'
                }}
              >
                {isExpanded ? 'View Less' : 'View More'}
              </div>
            )}

            <AchievementsBadgePopover
              open={Boolean(anchorEl)}
              data={badgeData}
              anchorEl={anchorEl}
              onExited={handleClearBadgeData}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BadgeAchievement;
